import { setCookie } from "cookies-next";

// external dependencies
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { signIn } from "next-auth/react";

// types

import { COOKIE_CONFIG } from "@/config";
import { GLOBAL_ERROR_MESSAGE } from "@/constants/validation-message";
import { IApiError } from "@/interface/apiError.types";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import loginSchema from "@/schema/login";

// internal utilities and services
import { loginUser } from "@/services/auth.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

const useLogin = () => {
  const router = useRouter();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onSubmit",
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Reset Google loading state when user returns via back/forward cache
  useEffect(() => {
    const handlePageShow = () => setGoogleLoading(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setGoogleLoading(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      // For login, we don't set any account type in sessionStorage
      // This will make the NextAuth callback set isSignup to false
      await signIn("google", { callbackUrl: "/callback" });
    } catch (error: any) {
      setGoogleLoading(false);
      showToast(TOAST_TYPES.error, "Google sign-in failed. Please try again.");
    }
  };

  const { mutate: login, isPending: loginLoading } = useMutation({
    mutationFn: loginUser,
    onSuccess: () => {
      setCookie(COOKIE_CONFIG.loggedIn, "true");
      showToast(TOAST_TYPES.success, "Login successful");
      router.refresh();
    },

    onError: (error: IApiError) => {
      const statusCode = error?.response?.status;
      const errorData = error?.response?.data;
      const firstError = errorData?.errors?.[0] as any;

      // Previous logic (400-only) — commented out; API now returns 423 for deactivated accounts
      // if (statusCode === 400) {
      //   const errorCode = firstError?.code;
      //   const errorMessage =
      //     (typeof firstError?.message === "string" ? firstError.message : null) ||
      //     (typeof errorData?.message === "string" ? errorData.message : null);
      //   const isDeactivatedError =
      //     (errorCode === 400 && typeof errorMessage === "string" && errorMessage.toLowerCase().includes("deactivated")) ||
      //     (typeof errorMessage === "string" && errorMessage.toLowerCase().includes("deactivated"));
      //   if (isDeactivatedError) {
      //     router.push("/restore");
      //     return;
      //   }
      // }

      // Current API: 423 Locked + { error: "account_deactivated", message: "..." }
      const errorMessage =
        (typeof firstError?.message === "string" ? firstError.message : null) ||
        (typeof errorData?.message === "string" ? errorData.message : null);
      const errorCodeOrType = (errorData as { error?: string })?.error;

      // const isDeactivatedError = 
      //     (errorCode === 400 && typeof errorMessage === "string" && errorMessage.toLowerCase().includes("deactivated")) ||
      //     (typeof errorMessage === "string" && errorMessage.toLowerCase().includes("deactivated"));
      const isDeactivatedError =
        statusCode === 423 &&
        (errorCodeOrType === "account_deactivated" ||
          (typeof errorMessage === "string" &&
            errorMessage.toLowerCase().includes("deactivated")));

      if (isDeactivatedError) {
        router.push("/restore");
        return;
      }

      // 403 + email_not_confirmed — the account exists but the OTP was
      // never entered. Send the user to the confirmation screen instead of
      // dead-ending on a toast; the resend button there gets a fresh code.
      const isEmailNotConfirmed =
        statusCode === 403 &&
        (errorCodeOrType === "email_not_confirmed" ||
          (typeof errorMessage === "string" &&
            errorMessage.toLowerCase().includes("confirm your email")));

      if (isEmailNotConfirmed) {
        showToast(
          TOAST_TYPES.error,
          errorMessage ?? "Please confirm your email address before logging in."
        );
        router.push(
          `/signup/confirm/${encodeURIComponent(form.getValues("email"))}`
        );
        return;
      }

      showToast(TOAST_TYPES.error, errorMessage ?? GLOBAL_ERROR_MESSAGE);

    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    login(values);
  };

  return {
    form,
    onSubmit,
    loginLoading,
    showPassword,
    togglePasswordVisibility,
    handleGoogleLogin,
    googleLoading,
  };
};

export default useLogin;
